import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { FormErrorMessage } from '@/components/form/form-error-message';
import {
  InputGroup,
  type InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';

type InputControlledProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  id: string;
  placeholder: string;
  type: string;
  children?: React.ReactElement<typeof InputGroupAddon>;
  ns?: string;
  onValueChange?: (value: number | string) => void;
} & React.ComponentProps<'input'>;

export function InputControlled<T extends FieldValues>({
  control,
  name,
  label,
  children,
  type,
  id,
  placeholder,
  ns = 'common',
  disabled,
  onValueChange,
  ...props
}: InputControlledProps<T>) {
  return (
    <div className='space-y-2'>
      {label && <Label htmlFor={name}>{label}</Label>}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <>
            <InputGroup data-disabled={disabled}>
              <InputGroupInput
                disabled={disabled}
                id={id}
                placeholder={placeholder}
                step='any'
                type={type}
                {...field}
                onChange={(e) => {
                  const value =
                    type === 'number' ? Number(e.target.value) : e.target.value;
                  field.onChange(value);
                  onValueChange?.(value);
                }}
                {...props}
              />
              {children && children}
            </InputGroup>
            <FormErrorMessage error={fieldState.error?.message} ns={ns} />
          </>
        )}
      />
    </div>
  );
}
